import React, { useState } from "react";
import { Tabs, Spin, Table, Card, Typography } from "antd";
import { useQuery } from "@apollo/client";
import { INTEGRATION_RECORD_COUNT_QUERY, DATA_IMPORT_RECORDS_QUERY } from "~/graphql/query";
import { compareAsc } from "date-fns";
import styles from "./reports-page.module.css";
import {
  FilterFilled,
  DatabaseOutlined,
  CloudSyncOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { DateRangeFilter } from "../../../components/date-range-filter";
import { FilterDropdownProps } from "antd/es/table/interface";
import { DataImportEntityType } from "~/gql/graphql";
import { ColumnType } from "antd/es/table";
import { TablePaginationConfig } from "antd/es/table";
import { FilterValue } from "antd/es/table/interface";

const { Title } = Typography;

const {
  ["loading-overlay"]: loadingOverlayClassName,
  ["spinner-container"]: spinnerContainerClassName,
  ["tab-content"]: tabContentClassName,
} = styles as any;

const { TabPane } = Tabs;

const relevantEntityTypes = [
  DataImportEntityType.OrganisationalUnit,
  DataImportEntityType.OfficeLocation,
  DataImportEntityType.FeeEarnerLevel,
  DataImportEntityType.FeeEarner,
  DataImportEntityType.User,
  DataImportEntityType.Client,
  DataImportEntityType.Rate,
  DataImportEntityType.ExternalMatter,
  DataImportEntityType.TimeEntry,
  DataImportEntityType.MatterPermissions,
  DataImportEntityType.MatterInvoice,
  DataImportEntityType.Currency,
];

export const IntegrationReportsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [entityTypeFilters, setEntityTypeFilters] = useState<DataImportEntityType[]>([]);

  const { data: integrationRecordCount, loading: integrationRecordCountLoading } = useQuery(
    INTEGRATION_RECORD_COUNT_QUERY
  );

  const { data: dataImportRecords, loading: dataImportRecordsLoading } = useQuery(
    DATA_IMPORT_RECORDS_QUERY,
    {
      variables: {
        input: {
          page: currentPage,
          pageSize: pageSize,
          startDate: dateRange[0] ? dateRange[0].toISOString() : undefined,
          endDate: dateRange[1] ? dateRange[1].toISOString() : undefined,
          entityTypes: entityTypeFilters,
        },
      },
    }
  );

  const recordCountColumns = [
    {
      title: "Entity",
      dataIndex: "entity",
      key: "entity",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
    },
  ];

  const recordCountData = integrationRecordCount?.integrationRecordCountQuery
    ? Object.entries(integrationRecordCount.integrationRecordCountQuery)
        .filter(([key]) => key !== "__typename")
        .map(([key, value]) => ({
          key,
          entity: key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim(),
          count: value,
        }))
    : [];

  const dataImportColumns: ColumnType<any>[] = [
    {
      title: "Entity Type",
      dataIndex: "entityType",
      key: "entityType",
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filters: relevantEntityTypes.map((value) => ({
        text: value,
        value,
      })),
      filteredValue: entityTypeFilters,
      onFilter: ((value: string | number | boolean, record: any) =>
        record.entityType === (value as string | number | boolean).toString()) as any,
    },
    {
      title: "Date / Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => new Date(text).toLocaleString(),
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      filteredValue: dateRange
        .filter((date): date is Date => date !== null)
        .map((date) => date.getDate()),
      filterDropdown: (props: FilterDropdownProps) =>
        filterCreatedAt({
          ...props,
          setDateRange: (range: [Date | null, Date | null]) => {
            setDateRange(range);
            setCurrentPage(1);
          },
        }),
      sorter: (a: any, b: any) => compareAsc(new Date(a.createdAt), new Date(b.createdAt)),
    },
    {
      title: "Created",
      dataIndex: "createdCount",
      key: "createdCount",
    },
    {
      title: "Updated",
      dataIndex: "updatedCount",
      key: "updatedCount",
    },
    {
      title: "Deleted",
      dataIndex: "deletedCount",
      key: "deletedCount",
    },
    {
      title: "Error Message",
      dataIndex: "errorMessage",
      key: "errorMessage",
      render: (text: string) => {
        if (!text || text.trim() === "") {
          return <span style={{ color: "var(--text-color-secondary)" }}>None</span>;
        }
        return <span style={{ color: "var(--text-color-secondary)" }}>{text}</span>;
      },
    },
  ];

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>
  ) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    setEntityTypeFilters((filters.entityType as DataImportEntityType[]) || []);
  };

  // Calculate summary statistics
  const totalRecords = recordCountData.reduce(
    (sum, item) => sum + (typeof item.count === "number" ? item.count : 0),
    0
  );
  const totalImports = dataImportRecords?.dataImportRecordsQuery.total || 0;

  return (
    <div
      style={{
        background: "var(--header-bg-color)",
        minHeight: "100vh",
        padding: "var(--padding-lg)",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          marginBottom: "var(--margin-sm)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Title level={5} style={{ margin: 0, color: "var(--heading-color)" }}>
          <DatabaseOutlined style={{ marginRight: "var(--margin-xs)" }} />
          Integration Reports
        </Title>
        <div style={{ display: "flex", gap: "var(--margin-lg)" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "var(--font-size-sm)", color: "var(--text-color-secondary)" }}>
              Total Records
            </div>
            <div
              style={{
                fontSize: "var(--heading-4-size)",
                color: "var(--success-color)",
                fontWeight: "bold",
              }}
            >
              {totalRecords}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "var(--font-size-sm)", color: "var(--text-color-secondary)" }}>
              Total Imports
            </div>
            <div
              style={{
                fontSize: "var(--heading-4-size)",
                color: "var(--primary-color)",
                fontWeight: "bold",
              }}
            >
              {totalImports}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Card
        style={{
          borderRadius: "12px",
          border: "none",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ position: "relative" }}>
          {(integrationRecordCountLoading || dataImportRecordsLoading) && (
            <div className={loadingOverlayClassName}>
              <div className={spinnerContainerClassName}>
                <Spin size="large" />
              </div>
            </div>
          )}
          <Tabs
            defaultActiveKey="recordCounts"
            size="large"
            tabBarStyle={{
              padding: "0 24px",
              margin: 0,
              background: "#fafafa",
              borderRadius: "12px 12px 0 0",
            }}
          >
            <TabPane
              tab={
                <span>
                  <BarChartOutlined style={{ marginRight: "var(--margin-xs)" }} />
                  Record Counts
                </span>
              }
              key="recordCounts"
            >
              <div className={tabContentClassName} style={{ width: "100%" }}>
                <Table
                  columns={recordCountColumns}
                  dataSource={recordCountData}
                  pagination={false}
                  tableLayout="fixed"
                  style={{ width: "100%" }}
                  rowClassName={(_record, index) => (index % 2 === 0 ? "" : "table-row-alternate")}
                />
              </div>
            </TabPane>
            <TabPane
              tab={
                <span>
                  <CloudSyncOutlined style={{ marginRight: "var(--margin-xs)" }} />
                  Data Import
                </span>
              }
              key="dataImport"
            >
              <div className={tabContentClassName} style={{ width: "100%" }}>
                <Table
                  columns={dataImportColumns}
                  dataSource={dataImportRecords?.dataImportRecordsQuery.items}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: dataImportRecords?.dataImportRecordsQuery.total,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  onChange={handleTableChange}
                  scroll={{ x: "max-content" }}
                  style={{ width: "100%" }}
                  rowClassName={(_record, index) => (index % 2 === 0 ? "" : "table-row-alternate")}
                />
              </div>
            </TabPane>
          </Tabs>
        </div>
      </Card>
    </div>
  );
};

const NULL_SELECTION: number[] = [];

interface ExtendedFilterDropdownProps {
  setSelectedKeys: (selectedKeys: React.Key[]) => void;
  confirm: () => void;
  clearFilters?: () => void;
  setDateRange: (range: [Date | null, Date | null]) => void;
}

export const filterCreatedAt = ({
  setSelectedKeys,
  confirm,
  clearFilters,
  setDateRange,
}: ExtendedFilterDropdownProps) => (
  <DateRangeFilter
    onApplyFilter={(value) => {
      if (value && value.from && value.to) {
        const fromTime = value.from.getTime();
        const toTime = value.to.getTime();
        setSelectedKeys([fromTime, toTime]);
        setDateRange([value.from, value.to]);
      } else {
        setSelectedKeys(NULL_SELECTION);
        setDateRange([null, null]);
      }
      confirm();
    }}
    onClearFilter={() => {
      clearFilters && clearFilters();
      setDateRange([null, null]);
      confirm();
    }}
  />
);
